import React from "react"
import {
  HStack, Text, VStack, IconButton, Box,
  useDisclosure,
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody, Portal,
  Button,
} from "@chakra-ui/react"
import {
  Control, useWatch, useController,
} from "react-hook-form"
import { ColorValue } from "sharedTypes/badge"
import {
  SolidLineIcon,
  DashedLineIcon,
  DottedLineIcon,
} from "components/BadgeBuilder/Icons"
import Icon from "components/elements/Icon"
import PropertyRow from "components/CanvasEditor/Inspector/PropertyRow"
import ToggleButtonGroup, { ToggleButtonGroupItem } from "components/ReactHookForm/ToggleButtonGroup"
import { formatMeasurement } from "components/BadgeBuilder/utils/units"
import NumberProperty from "components/CanvasEditor/Inspector/NumberProperty"
import ColorPicker from "components/CanvasEditor/Inspector/ColorPicker"
import { MIXED, useIsMixed } from "components/CanvasEditor/Inspector/MixedValues"
import { usePrinterSwatches } from "components/BadgeBuilder/hooks/usePrinterColorConstraints"
import { BadgeElementFormValues } from "components/BadgeBuilder/Panels/EditorPanel/BadgeSidePanelForm"

type Props = {
  control: Control<BadgeElementFormValues>
}

const BorderControl = ({ control }: Props) => {
  const swatches = usePrinterSwatches()
  const borderWidth = useWatch({ control, name: "border.width" }) ?? 0

  const borderColor = useWatch({ control, name: "border.color" }) ?? "#000000"

  const borderStyle = useWatch({ control, name: "border.style" }) ?? "solid"
  const widthMixed = useIsMixed("border.width")
  const colorMixed = useIsMixed("border.color")
  const styleMixed = useIsMixed("border.style")
  // The color and style only count where there's a border
  const mixed = widthMixed || (borderWidth > 0 && (colorMixed || styleMixed))
  const hasBorder = borderWidth > 0 || widthMixed

  const { isOpen, onOpen, onClose } = useDisclosure()

  const { field: borderWidthField } = useController({
    name: "border.width",
    control,
    defaultValue: 0,
  })

  const { field: borderColorField } = useController({
    name: "border.color",
    control,
    defaultValue: "#000000",
  })

  const borderStyleOptions = [
    {
      button: (selected: boolean, onClick: () => void) => (
        <ToggleButtonGroupItem
          selected={selected}
          onClick={onClick}
          title="Solid"
          size="sm"
        >
          <SolidLineIcon />
        </ToggleButtonGroupItem>
      ),
      value: "solid",
    },
    {
      button: (selected: boolean, onClick: () => void) => (
        <ToggleButtonGroupItem
          selected={selected}
          onClick={onClick}
          title="Dashed"
          size="sm"
        >
          <DashedLineIcon />
        </ToggleButtonGroupItem>
      ),
      value: "dashed",
    },
    {
      button: (selected: boolean, onClick: () => void) => (
        <ToggleButtonGroupItem
          selected={selected}
          onClick={onClick}
          title="Dotted"
          size="sm"
        >
          <DottedLineIcon />
        </ToggleButtonGroupItem>
      ),
      value: "dotted",
    },
  ]

  const getDisplayText = () => {
    if (mixed) return MIXED
    if (borderWidth === 0) return "Add..."

    const styleName = borderStyle.charAt(0).toUpperCase() + borderStyle.slice(1)

    return `${styleName} (${formatMeasurement(borderWidth, "mm", { precision: 1 })})`
  }

  const addBorder = () => {
    borderWidthField.onChange(0.5)
    borderColorField.onChange("#000000" as ColorValue)
    onOpen()
  }

  const clearBorder = () => {
    borderWidthField.onChange(0)
    onClose()
  }

  return (
    <PropertyRow label="Border">
      <HStack spacing={2}>
        <Popover
          isOpen={isOpen}
          onOpen={hasBorder ? onOpen : addBorder}
          onClose={onClose}
          placement="right-start"
          closeOnBlur
          closeOnEsc
        >
          <PopoverTrigger>
            <Button
              variant="outline"
              size="sm"
              flex={1}
              justifyContent="flex-start"
              bg="white"
              _hover={{ bg: "gray.50" }}
              _focus={{ bg: "gray.50", boxShadow: "none" }}
              borderColor="gray.300"
              px={2}
            >
              <HStack spacing={2}>
                {borderWidth > 0 && !colorMixed && (
                  <Box
                    w={3}
                    h={3}
                    borderRadius="sm"
                    bg={borderColor}
                    borderWidth={1}
                    borderColor="gray.400"
                    flexShrink={0}
                  />
                )}
                <Text
                  fontSize="sm"
                  color={hasBorder ? "black" : "gray.500"}
                  isTruncated
                  noOfLines={1}
                  maxW="100%"
                  textOverflow="ellipsis"
                >
                  {getDisplayText()}
                </Text>
              </HStack>
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent w="320px" zIndex={99999}>
              <PopoverArrow />
              <PopoverBody>
                <VStack spacing={4} p={2}>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="bold" mb={3}>Color</Text>
                    <ColorPicker
                      name="border.color"
                      control={control}
                      defaultValue="#000000"
                      {...swatches}
                    />
                  </Box>

                  <PropertyRow label="Width">
                    <NumberProperty
                      name="border.width"
                      control={control}
                      min={0.1}
                      max={5}
                      step={0.1}
                      precision={2}
                      size="sm"
                    />
                  </PropertyRow>

                  <PropertyRow label="Style">
                    <ToggleButtonGroup
                      name="border.style"
                      control={control}
                      columns={3}
                      spacing={2}
                    >
                      {borderStyleOptions}
                    </ToggleButtonGroup>
                  </PropertyRow>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
        {hasBorder && (
          <IconButton
            aria-label="Remove border"
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={clearBorder}
            fontSize="md"
            icon={<Icon icon="cross" />}
          />
        )}
      </HStack>
    </PropertyRow>
  )
}

export default BorderControl
